import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Brain, FileCheck, Users, ArrowRight, Building2, Store, CheckCircle, Search, ClipboardList, Lightbulb, Wrench } from 'lucide-react';
import { images } from '../data/mockData';

const Home = () => {
  const helpAreas = [
    {
      icon: Shield,
      title: 'Cybersecurity Understanding',
      description: 'We walk through how problems actually happen and how to prevent them — without technical jargon or scare tactics. You leave knowing what matters and what doesn\'t. Includes NIST/TAC-aligned readiness reviews.'
    },
    {
      icon: Brain,
      title: 'Practical AI Use',
      description: 'We show real ways AI can save time and reduce mistakes in daily work. No experiments, no hype — just what\'s actually useful.'
    },
    {
      icon: FileCheck,
      title: 'Compliance Readiness',
      description: 'Requirements become clear and manageable. We help you understand what to do, what to document, and why it matters so audits aren\'t stressful. NIST/TAC-aligned readiness reviews help you prepare effectively.'
    },
    {
      icon: Users,
      title: 'Staff Awareness Training',
      description: 'Training your team can remember and use. We explain security in a way that fits how people actually work day to day.'
    }
  ];

  const audiences = [
    {
      title: 'Local Businesses',
      description: 'Independent businesses trying to figure out what they actually need without someone trying to sell them something.',
      link: '/small-business'
    },
    {
      title: 'Restaurants',
      description: 'Restaurant owners dealing with the reality of turnover, tight margins, and payment security.',
      link: '/small-business'
    },
    {
      title: 'Nonprofits',
      description: 'Organizations doing important work with limited budgets who need straight answers.',
      link: '/services'
    },
    {
      title: 'Public Organizations',
      description: 'Texas schools, cities, and agencies working through TAC 202 and other compliance requirements.',
      link: '/public-entities'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-stone-100 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1e3a5f] leading-tight mb-4">
                We help you figure out what you actually need — and what you don't.
              </h1>
              <p className="text-xl text-gray-700 mb-4 leading-relaxed">
                Clear, practical technology guidance for small businesses and public organizations in West Texas.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                We don't sell software and we don't lock clients into contracts. Our role is to help people make good decisions, understand risk, and avoid unnecessary expense.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#c84630] text-white font-medium rounded-md hover:bg-[#a83825] transition-colors"
                >
                  Schedule a Consultation
                  <ArrowRight size={18} className="ml-2" aria-hidden="true" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#1e3a5f] text-[#1e3a5f] font-medium rounded-md hover:bg-[#1e3a5f] hover:text-white transition-colors"
                >
                  View Services
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src={images.hero.home}
                alt="Professional consultation"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who We Help Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              Who We Help
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We work with organizations across West Texas that need practical technology guidance.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Public Entities */}
            <div className="bg-stone-50 rounded-xl p-8 border border-gray-200">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center mr-4">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#1e3a5f]">Public Entities</h3>
              </div>
              <ul className="space-y-3">
                {['Cities', 'Counties', 'Special districts', 'Education institutions', 'Government agencies'].map((item, idx) => (
                  <li key={idx} className="flex items-center text-gray-700">
                    <CheckCircle className="w-5 h-5 text-[#c84630] mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/public-entities" 
                className="inline-flex items-center mt-6 text-[#c84630] font-medium hover:text-[#a83825]"
              >
                Learn more <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
            
            {/* Small Businesses */}
            <div className="bg-stone-50 rounded-xl p-8 border border-gray-200">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center mr-4">
                  <Store className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#1e3a5f]">Small Businesses</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Professional services',
                  'Healthcare practices',
                  'Local retail and service companies',
                  'Growing organizations needing better systems'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center text-gray-700">
                    <CheckCircle className="w-5 h-5 text-[#c84630] mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/small-business" 
                className="inline-flex items-center mt-6 text-[#c84630] font-medium hover:text-[#a83825]"
              >
                Learn more <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              How We Work
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A straightforward approach to understanding and improving your technology.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Search,
                step: '01',
                title: 'Discovery',
                description: 'Understand your organization, goals, and current systems.'
              },
              {
                icon: ClipboardList,
                step: '02',
                title: 'Assessment',
                description: 'Evaluate tools, workflows, and opportunities for improvement.'
              },
              {
                icon: Lightbulb,
                step: '03',
                title: 'Strategy',
                description: 'Provide clear recommendations and practical options.'
              },
              {
                icon: Wrench,
                step: '04',
                title: 'Implementation Support',
                description: 'Assist with execution, vendors, or system transitions.'
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 relative">
                <span className="absolute top-4 right-4 text-4xl font-bold text-gray-100">{item.step}</span>
                <div className="w-12 h-12 bg-[#f5e6e0] rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#c84630]" />
                </div>
                <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/how-we-work"
              className="inline-flex items-center px-6 py-3 border-2 border-[#1e3a5f] text-[#1e3a5f] font-medium rounded-md hover:bg-[#1e3a5f] hover:text-white transition-colors"
            >
              Learn more about our process
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Clients Work With Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-6">
                Why Clients Work With Us
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We focus on what's right for your organization, not what's profitable for vendors.
              </p>
              <ul className="space-y-4">
                {[
                  'Independent consulting with no vendor bias',
                  'Clear and practical recommendations',
                  'Experience working with public entities and small businesses',
                  'Technology explained in plain language',
                  'Focus on long-term solutions rather than quick fixes'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <div className="w-6 h-6 bg-[#c84630] rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link
                  to="/about"
                  className="inline-flex items-center text-[#c84630] font-medium hover:text-[#a83825]"
                >
                  Learn more about us <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src={images.hero.consultation}
                alt="Professional consulting discussion"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Help With */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              What We Help With
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              What we help you feel confident about:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {helpAreas.map((area, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#f5e6e0] rounded-lg mb-4">
                  <area.icon size={32} className="text-[#c84630]" />
                </div>
                <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">
                  {area.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              Where We Help
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The kinds of organizations we work with across West Texas
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {audiences.map((audience, index) => (
              <div key={index} className="bg-white p-8 rounded-lg border border-gray-200 hover:border-[#c84630] transition-colors">
                <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">
                  {audience.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {audience.description}
                </p>
                <Link
                  to={audience.link}
                  className="inline-flex items-center text-[#c84630] hover:text-[#a83825] font-medium transition-colors"
                  aria-label={`Learn more about services for ${audience.title}`}
                >
                  Learn more
                  <ArrowRight size={16} className="ml-1" aria-hidden="true" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={images.hero.ourApproach}
                alt="Personal consultation"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-6">
                Our Approach
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">
                    We meet in person
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    These conversations are easier face-to-face. We come to you.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">
                    We speak plainly
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    If we can't explain something in regular language, we probably don't understand it ourselves.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">
                    We don't sell software
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    We have nothing to sell except our time and judgment. That keeps things simple.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">
                    No contracts required
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Work with us when it makes sense. Stop when it doesn't.
                  </p>
                </div>
              </div>
              <Link
                to="/how-we-work"
                className="inline-flex items-center mt-8 text-[#c84630] hover:text-[#a83825] font-medium transition-colors"
              >
                See how we work
                <ArrowRight size={18} className="ml-2" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Start with a conversation
          </h2>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">
            Tell us what you're trying to figure out. We'll have an honest conversation about whether we can help and what that would look like.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#c84630] text-white font-medium rounded-md hover:bg-[#a83825] transition-colors text-lg"
          >
            Get in touch
            <ArrowRight size={20} className="ml-2" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
